import { noop } from 'lodash';
import Router from 'next/router';
import { cloneElement, type ReactElement, type ReactNode, useCallback, useEffect } from 'react';

import {
  ActionSheet,
  ActionSheetHeader,
  type ActionSheetOption,
  renderActionSheetItems,
} from '@air/primitive-action-sheet';
import {
  DropdownMenu,
  type DropdownMenuOption,
  type DropdownMenuProps,
  renderDropdownItems,
} from '@air/primitive-dropdown-menu';
import { useBreakpointsContext } from '@air/provider-media-query';
import { useAirModal } from '@air/provider-modal';

/*
  this method can be used to manually hide menu dropdown
  useful e.g. when you have button in menu and you want to hide it after click
  it's hacky, but I didn't find any other way to hide it. It simulates mouseDown and mouseUp events on document body
  it can't be just 'click', since dropdown is hidden on mousedown event
  mouseup is required, because otherwise mouse scroll is fired
 */
export const manuallyHideDropdown = () => {
  const mouseDownEvt = new MouseEvent('mousedown', {
    view: window,
    bubbles: true,
  });

  const mouseUpEvt = new MouseEvent('mouseup', {
    view: window,
    bubbles: true,
  });

  document.body.dispatchEvent(mouseDownEvt);
  document.body.dispatchEvent(mouseUpEvt);
};

export type DropdownMenuActionSheetProps = {
  content?: ReactNode;
  isTitleHidden?: boolean;
  onChange?: (value: boolean) => void;
  options?: ActionSheetOption[];
  title?: string | ReactNode;
  container?: HTMLElement | null;
};

export const DropdownMenuActionSheet = ({
  content,
  isTitleHidden,
  onClose = noop,
  onChange = noop,
  title,
  options,
  ...restOfProps
}: Omit<AirModalProps<DropdownMenuActionSheetProps>, 'children'>) => {
  const { isAboveMediumScreen } = useBreakpointsContext();

  useEffect(() => {
    if (isAboveMediumScreen) {
      onClose();
    }
  }, [isAboveMediumScreen, onClose]);

  const handleClose = useCallback(() => {
    onClose();
    onChange?.(false);
  }, [onClose, onChange]);

  return (
    <ActionSheet {...restOfProps} onClose={handleClose}>
      {!isTitleHidden ? <ActionSheetHeader onClose={handleClose}>{title}</ActionSheetHeader> : null}
      {content}
      {renderActionSheetItems({ options })}
    </ActionSheet>
  );
};

export type DropdownMenuWithActionSheetProps = Pick<DropdownMenuProps, 'align' | 'onCloseAutoFocus' | 'size'> &
  Pick<DropdownMenuActionSheetProps, 'isTitleHidden' | 'onChange' | 'title'> & {
    children?: ReactNode;
    dropdownClassName?: string;
    'data-testid'?: string;
    options?: DropdownMenuOption[];
    trigger: ReactElement;
    container?: HTMLElement | null;
    'data-theme'?: 'dark' | 'light';
  };

/**
 * This component conditional renders ActionSheet and DropdownMenu based
 * on the current screen size and should be only be used for menus that
 * renders on mobile. Otherwise, use `DropdownMenu` from `@air/zephyr`.
 */
export const DropdownMenuWithActionSheet = ({
  children,
  align,
  'data-testid': dataTestId,
  isTitleHidden,
  onChange = noop,
  onCloseAutoFocus,
  options,
  size,
  title,
  trigger,
  container,
  'data-theme': dataTheme,
  dropdownClassName,
}: DropdownMenuWithActionSheetProps) => {
  const [showActionSheet, close] = useAirModal(DropdownMenuActionSheet);
  const { isAboveMediumScreen } = useBreakpointsContext();
  const isDropdown = isAboveMediumScreen || !!container;

  useEffect(() => {
    const closeActionSheet = () => {
      close();
      if (!isDropdown) {
        onChange(false);
      }
    };

    Router.events.on('routeChangeComplete', closeActionSheet);

    return () => Router.events.off('routeChangeComplete', closeActionSheet);
  }, [close, isDropdown, onChange]);

  return (
    <>
      {/*Action sheet does not work on fullscreen, and it requires more refactor to pass it there.
         This is a temporary solution to avoid breaking the app.
      */}
      {isDropdown ? (
        <DropdownMenu
          container={container}
          className={dropdownClassName}
          data-testid={dataTestId}
          onOpenChange={onChange}
          onCloseAutoFocus={onCloseAutoFocus}
          align={align}
          size={size}
          trigger={trigger}
          data-theme={dataTheme}
        >
          {children}
          {renderDropdownItems({ options })}
        </DropdownMenu>
      ) : (
        cloneElement(trigger, {
          onClick: (event: MouseEvent) => {
            event.stopPropagation();
            showActionSheet({
              options: options?.map((option) => {
                if (option.type === 'item') {
                  return {
                    ...option,
                    onSelect: () => {
                      close();
                      option.onSelect?.();
                    },
                  };
                }

                return option;
              }),
              isTitleHidden,
              content: children,
              onChange,
              title,
            });
            onChange(true);
          },
        })
      )}
    </>
  );
};
